import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './ReportsDashboard.css';
import { Description as DescriptionIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  ListItemText,
  OutlinedInput,
  Chip,
  Box,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as _props from '../../_redux/_services/JobPostingSlice';
import * as _clientser from '../../_redux/_services/ClientsSlice';
import { GetUserId, GetUserInfo } from '../../_utilities/getUserInfo';
import { defaultApplicantsData } from '../../_utilities/defaultfieldsModel';
import { format } from 'date-fns';
import { reportTypesEnum } from '../../constants/constants';
import { GetCustomFields } from '../../_redux/_apiHelpers/UserManagerApihelper';
import { isValidDate } from '../../_utilities/utilities';
import { getReportConfig, getFilterOptions, getOperations, getFilterType, getFilterSource, getFilterApiKey, getOperationApiValue, getFilterFieldType } from './ReportConfig';
import ScheduleReport from './ScheduleReport';
import { setReportPayload, clearReportPayload } from '../../_redux/ReportDataSlice';
import RelativeDateRangePicker from '../../components/RelativeDateRangePicker/RelativeDateRangePicker';

const ScheduleReportsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('');
  const [reportName, setReportName] = useState('');
  const [activeLifecycle, setActiveLifecycle] = useState('');
  const [criteria, setCriteria] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isCustomFieldChecked, setIsCustomFieldChecked] = useState(false);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [applicantCustomFields, setApplicantCustomFields] = useState([]);
  const [shouldReinit, setShouldReinit] = useState(false);
  const [showScheduleReport, setShowScheduleReport] = useState(false);

  const [clientCustomFields, setClientCustomFields] = useState([]);

  // Redux selectors for filter dropdown data
  const recruiterList = useSelector((state) => state.GetManagerTeamlead.recruiter);
  const clientList = useSelector((state) => state.FilterClients.data);
  const jobsList = useSelector((state) => state.Listjobpostings.data);

  const reportConfig = getReportConfig(reportType);
  const filterOptionsDynamic = getFilterOptions(reportType);

  // Local state for dropdown options
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [jobsOptions, setJobsOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourcedFromOptions, setSourcedFromOptions] = useState([]);
  const [clientStatusOptions] = useState([
    { id: 'Active', name: 'Active' },
    { id: 'Inactive', name: 'Inactive' },
  ]);
  const [clientLocationOptions, setClientLocationOptions] = useState([]);
  const [dropdownSearch, setDropdownSearch] = useState('');

  const reportTypes = [reportTypesEnum.CANDIDATE, reportTypesEnum.CLIENT];

  const [newCriterion, setNewCriterion] = useState({
    filterBy: '',
    operation: '',
    values: [],
  });

  const validationSchema = Yup.object({
    reportType: Yup.string().required('Report Type is required'),
    reportName: Yup.string().required('Report Name is required'),
    filterBy: Yup.string().when([], {
      is: () => criteria.length === 0,
      then: (schema) => schema.required('Filter By is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    operation: Yup.string().when([], {
      is: () => criteria.length === 0,
      then: (schema) => schema.required('Operation is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    values: Yup.array().when([], {
      is: () => criteria.length === 0,
      then: (schema) =>
        schema.min(1, 'Please select at least one value'),
      otherwise: (schema) => schema.notRequired(),
    }),
    activeLifecycle: Yup.string().required('Please select an active lifecycle'),
  });

  // Initialize static options from defaultfieldsModel
  useEffect(() => {
    // Get Candidate Status options
    const candidateStatusField = defaultApplicantsData.find(
      (field) => field.fieldName === 'Candidate status'
    );
    if (candidateStatusField) {
      const statusOpts = candidateStatusField.customValues.map((status) => ({
        id: typeof status === 'object' ? status.id : status,
        name: typeof status === 'object' ? status.internal : status,
      }));
      setStatusOptions(statusOpts);
    }

    // Get Sourced From options
    const sourceField = defaultApplicantsData.find(
      (field) => field.fieldName === 'Source'
    );
    if (sourceField) {
      const sourceOpts = sourceField.customValues.map((source) => ({
        id: source,
        name: source,
      }));
      setSourcedFromOptions(sourceOpts);
    }
  }, []);

  // Fetch recruiters, clients, and jobs on component mount (for filter dropdowns)
  useEffect(() => {
    async function fetchData() {
      const userId = await GetUserId();
      if (userId) {
        // Fetch recruiters
        dispatch(_props.getManagerTeamleadAsync(' ', userId));
        // Fetch clients
        dispatch(_clientser.filterClientsAsync(''));
        // Fetch jobs
        dispatch(
          _props.listjobpostingsAsync({ limit: 1000, offset: 1 }, 'list')
        );
        dispatch(clearReportPayload());
      }
    }
    fetchData();
  }, [dispatch]);

  // Process recruiter list when it changes
  useEffect(() => {
    if (recruiterList && recruiterList.length > 0) {
      const formattedRecruiters = recruiterList.map((recruiter) => ({
        id: recruiter.id.toString(),
        name: `${recruiter.firstName} ${recruiter.lastName}`,
        emailId: recruiter.emailId,
      }));
      setRecruiterOptions(formattedRecruiters);
    }
  }, [recruiterList]);

  // Process client list when it changes
  useEffect(() => {
    if (clientList && clientList.length > 0) {
      const activeClients = clientList
        .filter((client) => client.status === 'Active')
        .map((client) => ({
          id: client.id.toString(),
          name: client.clientName,
        }));
      setClientOptions(activeClients);

      const locationSet = new Set();
      clientList.forEach((c) => {
        if (c.location) locationSet.add(String(c.location).trim());
      });
      const locOpts = Array.from(locationSet)
        .filter(Boolean)
        .sort()
        .map((loc) => ({ id: loc, name: loc }));
      setClientLocationOptions(locOpts);
    }
  }, [clientList]);

  // Process jobs list when it changes
  useEffect(() => {
    // jobsList has structure { rows: [...], count: ... }
    if (jobsList && jobsList.rows && jobsList.rows.length > 0) {
      const formattedJobs = jobsList.rows.map((job) => ({
        id: job.id.toString(),
        name: job.jobCode ? `${job.jobTitle} (${job.jobCode})` : job.jobTitle,
        jobCode: job.jobCode,
      }));
      setJobsOptions(formattedJobs);
    }
  }, [jobsList]);

  const isClientReport = reportType === reportTypesEnum.CLIENT;
  const customFieldsModuleId = isClientReport ? '3' : '2';

  const handleCustomFieldToggle = async () => {
    const newChecked = !isCustomFieldChecked;
    setIsCustomFieldChecked(newChecked);

    if (newChecked) {
      setCustomFieldsLoading(true);
      try {
        const userId = GetUserId();
        const response = await GetCustomFields(userId);
        if (response?.data?.customfeilds) {
          const allCustom = response.data.customfeilds;

          const moduleFields = allCustom
            .filter((f) => String(f.moduleId) === customFieldsModuleId && f.fieldType !== 'default')
            .map((f) => ({
              displayName: f.fieldName,
              mappingName: `C:${f.fieldName}`,
              isCustomField: true,
              customFieldId: f.id,
              dataType: f.dataType,
              fieldType: f.fieldType,
              moduleId: f.moduleId,
            }));
          if (isClientReport) {
            setClientCustomFields(moduleFields);
          } else {
            setApplicantCustomFields(moduleFields);
          }
        }
      } catch (error) {
        console.error('Error fetching custom fields:', error);
      } finally {
        setCustomFieldsLoading(false);
      }
    } else {
      const existingCustom = isClientReport ? clientCustomFields : applicantCustomFields;
      const customMappingNames = existingCustom.map((f) => f.mappingName);
      setSelectedFields((prev) =>
        prev.filter((f) => !customMappingNames.includes(f.mappingName))
      );
      if (isClientReport) {
        setClientCustomFields([]);
      } else {
        setApplicantCustomFields([]);
      }
    }
  };

  // Get value options based on selected filter
  const getValueOptions = () => {
    const source = getFilterSource(reportType, newCriterion.filterBy);
    const sourceMap = {
      recruiterOptions,
      clientOptions,
      jobsOptions,
      statusOptions,
      sourcedFromOptions,
      clientStatusOptions,
      clientLocationOptions,
    };
    return sourceMap[source] || [];
  };

  // Get the label for the value dropdown
  const getValueLabel = () => {
    switch (newCriterion.filterBy) {
      case 'Client':
        return 'Client Name';
      case 'Jobs':
        return 'Job Title';
      case 'Sourced From':
        return 'Source';
      default:
        return newCriterion.filterBy || 'Select Value';
    }
  };

  // Check if a filter + operation combination already exists in criteria
  const isFilterOperationUsed = (filterBy, operation) => {
    return criteria.some(
      (c) => c.filterBy === filterBy && c.operation === operation
    );
  };

  // Get available operations for a filter (exclude already used combinations)
  const getAvailableOperations = () => {
    if (!newCriterion.filterBy) return [];

    const operationsForFilter = getOperations(reportType, newCriterion.filterBy);

    return operationsForFilter.map((op) => ({
      value: op,
      disabled: isFilterOperationUsed(newCriterion.filterBy, op),
    }));
  };

  // Check if operation is single select (Is for Client only)
  const isSingleSelect = () => {
    return getFilterType(reportType, newCriterion.filterBy) === "single";
  };

  // Get display label for selected values
  const getValuesDisplayLabel = (values, filterBy) => {
    const fieldType = getFilterFieldType(reportType, filterBy);
    if (fieldType === 'text' || fieldType === 'number' || fieldType === 'date') {
      return values.join(' to ');
    }
    const source = getFilterSource(reportType, filterBy);
    const sourceMap = {
      recruiterOptions,
      clientOptions,
      jobsOptions,
      statusOptions,
      sourcedFromOptions,
      clientStatusOptions,
      clientLocationOptions,
    };
    const options = sourceMap[source] || [];
    return values
      .map((val) => {
        const option = options.find((opt) => opt.id === val);
        return option ? option.name : val;
      })
      .join(', ');
  };

  const handleAddCriteria = (formik) => {
    const isBetween = newCriterion.operation === 'Between';
    const filledValues = newCriterion.values.filter((v) => v !== '' && v !== undefined && v !== null);
    const hasValue = isBetween ? filledValues.length >= 2 : filledValues.length > 0;
    if (criteria.length === 0) {
    let hasError = false;
      if (!newCriterion.filterBy) {
        formik.setFieldError('filterBy', 'Filter By is required');
        hasError = true;
      }
      if (!newCriterion.operation) {
        formik.setFieldError('operation', 'Operation is required');
        hasError = true;
      }
      if (!hasValue) {
        formik.setFieldError('values', isBetween ? 'Both From and To values are required' : 'Please select at least one value');
        hasError = true;
      }
      if (hasError) return;
    }

    if (newCriterion.filterBy && newCriterion.operation && hasValue) {
      // Check for duplicate filter + operation
      if (
        isFilterOperationUsed(newCriterion.filterBy, newCriterion.operation)
      ) {
        alert(
          `${newCriterion.filterBy} with "${newCriterion.operation}" operation is already added. Please use a different operation or remove the existing one.`
        );
        return;
      }

      const valuesLabel = getValuesDisplayLabel(
        filledValues,
        newCriterion.filterBy
      );

      setCriteria([
        ...criteria,
        {
          ...newCriterion,
          values: filledValues,
          id: Date.now(),
          valuesLabel: valuesLabel,
        },
      ]);
      formik.setFieldValue('filterBy', '');
      formik.setFieldValue('operation', '');
      formik.setFieldValue('values', []);
      setNewCriterion({
        filterBy: '',
        operation: '',
        values: [],
      });
    }
  };

  const handleRemoveCriteria = (id) => {
    setCriteria(criteria.filter((c) => c.id !== id));
  };

  const handleClearCriteriaForm = () => {
    setNewCriterion({
      filterBy: '',
      operation: '',
      values: [],
    });
  };

  const handleFieldToggle = (field) => {
    if (isDragging) return;

    const isSelected = selectedFields.some(
      (f) => f.mappingName === field.mappingName
    );
    if (isSelected) {
      setSelectedFields(
        selectedFields.filter((f) => f.mappingName !== field.mappingName)
      );
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleClearFields = () => {
    setSelectedFields([]);
  };

  const handleDragStart = (e, index) => {
    setIsDragging(true);
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggingIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (dragIndex !== dropIndex) {
      const items = [...selectedFields];
      const draggedItem = items[dragIndex];

      items.splice(dragIndex, 1);
      items.splice(dropIndex, 0, draggedItem);

      setSelectedFields(items);
    }

    setDragOverIndex(null);
    setDraggingIndex(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    setDraggingIndex(null);
    setIsDragging(false);
  };

  const handleFilterByChange = (value) => {
    setNewCriterion({
      filterBy: value,
      operation: '',
      values: [],
    });
  };

  const handleOperationChange = (value) => {
    setNewCriterion({
      ...newCriterion,
      operation: value,
      values: [],
    });
  };

  const handleValuesChange = (event) => {
    const {
      target: { value },
    } = event;
    setNewCriterion({
      ...newCriterion,
      values: typeof value === 'string' ? value.split(',') : value,
    });
  };

  // Render selected values as chips
  const renderSelectedValues = (selected) => {
    const options = getValueOptions();
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => {
          const option = options.find((opt) => opt.id === value);
          return (
            <Chip
              key={value}
              label={option ? option.name : value}
              size="small"
            />
          );
        })}
      </Box>
    );
  };

  // Map report type to API value
  const getReportTypeValue = (type) => {
    switch (type) {
      case 'Candidate Reports':
        return 'candidate';
      case 'Client Reports':
        return 'client';
      case 'Recruitment Reports':
        return 'job'; // Backend expects 'job' for recruitment reports
      default:
        return '';
    }
  };

  // Map filter by to API value
  const getFilterTypeValue = (filterBy) => {
    return getFilterApiKey(reportType, filterBy);
  };

  // Map operation to API value
  const getOperationValue = (operation) => {
    return getOperationApiValue(reportType, operation);
  };

  // Handle Request Report button click
  const handleRequestReport = async () => {
    try {
      const userInfo = await GetUserInfo();
      const createdByUserId = userInfo?.[0]?.userId || 'unknown';

      // Prepare selected fields as flat array (order matches table preview order)
      const selectedFieldsPayload = selectedFields.map(
        (field) => field.mappingName
      );

      // Prepare criteria - send all criteria as array
      const criteriaPayload = criteria.map((criterion) => ({
        filterType: getFilterTypeValue(criterion.filterBy),
        filterOperation: getOperationValue(criterion.operation),
        filterValues: criterion.values,
      }));

      // Build the payload
      const payload = {
        reportType: getReportTypeValue(reportType),
        reportName: reportName,
        tDays: activeLifecycle,
        selectedFields: selectedFieldsPayload,
        criteria: criteriaPayload,
        createdBy: createdByUserId,
      };
      setReportData(payload);
      dispatch(setReportPayload(payload));
      setShowScheduleReport(true);
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  useEffect(() => {
    if(criteria.length > 0 && reportType && reportName && activeLifecycle) {
      setShouldReinit(true);
    } else {
      setShouldReinit(false);
    }
  }, [criteria, reportType, reportName, activeLifecycle]);

  const getSectionFields = (sectionKey) => {
    const baseFields = reportConfig?.fields?.[sectionKey] || [];
    if (sectionKey === 'candidateFields') {
      return [...baseFields, ...applicantCustomFields];
    }
    if (sectionKey === 'jobFields' && isClientReport) {
      return [...baseFields, ...clientCustomFields];
    }
    return baseFields;
  };

  const renderSelectFieldsUI = (formik, fields, columns) => {
    const chunkSize = Math.ceil(fields.length / columns);
    const chunks = Array.from({ length: columns }, (_, i) =>
      fields.slice(i * chunkSize, (i + 1) * chunkSize)
    );

    return chunks.map((col, index) => (
      <div key={index} className="fields-column">
        {col.map((field) => (
          <FormControlLabel
            key={field.mappingName}
            control={
              <Checkbox
                checked={selectedFields.some(
                  (f) => f.mappingName === field.mappingName
                )}
                onChange={() => {
                  handleFieldToggle(field);
                  formik.setFieldError('selectedFields', '');
                }}
                className="field-checkbox"
              />
            }
            label={field.displayName}
            className={`field-checkbox-label${
              field.isCustomField ? ' field-checkbox-label--custom' : ''
            }`}
          />
        ))}
      </div>
    ));
  };

  return (
    <>
      <Formik
      initialValues={{
        reportType,
        reportName,
        criteria: '',
        selectedFields: '',
        filterBy: '',
        operation: '',
        values: [],
        criteriaLength: criteria.length,
        activeLifecycle,
      }}
      enableReinitialize={shouldReinit}
      validationSchema={validationSchema}
      onSubmit={(values, { setFieldError }) => {
        if (criteria.length === 0) {
          setFieldError('criteria', 'Please add at least one criteria');
          return;
        }
        if (selectedFields.length === 0) {
          setFieldError('selectedFields', 'Please select at least one field');
          return;
        }
        handleRequestReport();
      }}
    >
      {(formik) => (
        <div>
        {/* Header Section */}
        <div className="reports-dashboard-header">
          <div className="header-left">
            <div className="header-icon">
              <DescriptionIcon />
            </div>
            <div className="header-content">
              <h1 className="header-title">Reports</h1>
              <p className="header-subtitle">
                Choose a report to select data and generate
              </p>
            </div>
          </div>
          <button
            className="view-reports-btn"
            onClick={() => navigate('/ats/layout/reports/centre')}
          >
            View Reports
          </button>
        </div>

        {/* Filter Section */}
        <div className="reports-filter-section">
          <div className="filter-row">
            <div className="filter-field">
              <label className="filter-label">Report Type *</label>
              <FormControl fullWidth
                error={formik.touched.reportType && Boolean(formik.errors.reportType)}
              >
                <Select
                   value={formik.values.reportType}
                    onChange={(e) => {
                      formik.resetForm({
                        values: {
                          ...formik.initialValues,
                          reportType: e.target.value
                        }
                      });
                      setReportType(e.target.value);
                      setReportName('');
                      setCriteria([]);
                      setNewCriterion({filterBy: '', operation: '', values: []});
                      setSelectedFields([]);
                      setActiveLifecycle("")
                      setIsCustomFieldChecked(false);
                      setApplicantCustomFields([]);
                      setClientCustomFields([]);
                    }}
                  displayEmpty
                  onBlur={formik.handleBlur}
                  name="reportType"
                  className="filter-select"
                  MenuProps={{ variant: 'menu' }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: '#999' }}>Select Report Type</span>
                      );
                    }
                    return selected;
                  }}
                >
                  {reportTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ErrorMessage name="reportType" component="div" className="error-message" />
            </div>

            <div className="filter-field">
              <label className="filter-label">Report Name *</label>
              <TextField
                value={formik.values.reportName}
                onBlur={formik.handleBlur}
                name="reportName"
                error={formik.touched.reportName && Boolean(formik.errors.reportName)}
                onChange={(e) => {
                  formik.setFieldValue('reportName', e.target.value);
                  setReportName(e.target.value);
                }}
                placeholder="Enter Report Name"
                fullWidth
                className="filter-input"
                variant="outlined"
                size="small"
              />
              <ErrorMessage name="reportName" component="div" className="error-message" />
            </div>

            <div className="filter-field">
              <RelativeDateRangePicker
                onReceiveProps={(data)=>{ 
                  setActiveLifecycle(data);
                  formik.setFieldValue('activeLifecycle', data);
                }}
                error={formik.touched.activeLifecycle && Boolean(formik.errors.activeLifecycle)}
                helperText={formik.touched.activeLifecycle && formik.errors.activeLifecycle}
              />
            </div>
          </div>
        </div>

        {/* Select Criteria Section */}
        <div className="select-criteria-section">
          <div className="section-title-row">
            <h3 className="section-title">Select Criteria</h3>
          </div>

          {!reportType ? (
            <p className="no-report-type-message">
              Please select a report type to see available criteria
            </p>
          ) : (
          <>
          <div className="criteria-inputs">
            <div className="criteria-field">
              <label className="criteria-label">Filter By *</label>
              <FormControl fullWidth error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}>
                <Select
                  value={newCriterion.filterBy}
                  onChange={(e) => {
                    handleFilterByChange(e.target.value);
                    formik.setFieldValue('filterBy', e.target.value);
                  }}
                  displayEmpty
                  onBlur={formik.handleBlur}
                  name="filterBy"
                  className="criteria-select"
                  MenuProps={{ variant: 'menu' }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: '#999' }}>Select Filter</span>;
                    }
                    return selected;
                  }}
                >
                  {filterOptionsDynamic.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {criteria.length === 0 && (
                <ErrorMessage name="filterBy" component="div" className="error-message" />
              )}
            </div>

            <div className="criteria-field">
              <label className="criteria-label">Select Operation *</label>
              <FormControl fullWidth error={newCriterion.operation.length === 0}>
                <Select
                  value={newCriterion.operation}
                  onChange={(e) => {
                    handleOperationChange(e.target.value);
                    formik.setFieldValue('operation', e.target.value);
                  }}
                  displayEmpty
                  disabled={!newCriterion.filterBy}
                  className="criteria-select"
                  onBlur={formik.handleBlur}
                  name="operation"
                  MenuProps={{ variant: 'menu' }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: '#999' }}>Select Operation</span>
                      );
                    }
                    return selected;
                  }}
                >
                  {getAvailableOperations().map((op) => (
                    <MenuItem
                      key={op.value}
                      value={op.value}
                      disabled={op.disabled}
                    >
                      {op.value}
                      {op.disabled && ' (Already used)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {criteria.length === 0 && (
                <ErrorMessage name="operation" component="div" className="error-message" />
              )}
            </div>

            <div className="criteria-field">
              <label className="criteria-label">{getValueLabel()} *</label>
              {(() => {
                const fieldType = getFilterFieldType(reportType, newCriterion.filterBy);
                const op = newCriterion.operation;
                const disabled = !newCriterion.filterBy || !newCriterion.operation;

                if (fieldType === 'text' || fieldType === 'number') {
                  const isBetween = op === 'Between';
                  const inputType = fieldType === 'number' ? 'number' : 'text';
                  const setIdx = (idx, raw) => {
                    const next = [...newCriterion.values];
                    while (next.length <= idx) next.push('');
                    next[idx] = raw;
                    setNewCriterion((prev) => ({ ...prev, values: next }));
                    formik.setFieldValue('values', next.filter((v) => v !== undefined && v !== ''));
                  };
                  if (isBetween) {
                    return (
                      <div className="criteria-between-row">
                        <TextField
                          type={inputType}
                          size="small"
                          fullWidth
                          placeholder="From"
                          disabled={disabled}
                          value={newCriterion.values[0] || ''}
                          onChange={(e) => setIdx(0, e.target.value)}
                          className="criteria-input"
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0 }}
                        />
                        <TextField
                          type={inputType}
                          size="small"
                          fullWidth
                          placeholder="To"
                          disabled={disabled}
                          value={newCriterion.values[1] || ''}
                          onChange={(e) => setIdx(1, e.target.value)}
                          className="criteria-input"
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0 }}
                        />
                      </div>
                    );
                  }
                  return (
                    <TextField
                      type={inputType}
                      size="small"
                      fullWidth
                      placeholder="Enter value"
                      disabled={disabled}
                      value={newCriterion.values[0] || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNewCriterion((prev) => ({ ...prev, values: v === '' ? [] : [v] }));
                        formik.setFieldValue('values', v === '' ? [] : [v]);
                      }}
                      className="criteria-input"
                      onWheel={(e) => e.target.blur()}
                      inputProps={{ min: 0 }}
                    />
                  );
                }

                if (fieldType === 'date') {
                  const isPreset = op === 'Last 7 Days' || op === 'Last 30 Days';
                  const isRange = op === 'Between' || op === 'Custom Range';
                  if (isPreset) {
                    const days = op === 'Last 7 Days' ? 7 : 30;
                    const today = new Date();
                    const start = new Date();
                    start.setDate(today.getDate() - days);
                    const startStr = format(start, 'yyyy-MM-dd');
                    const endStr = format(today, 'yyyy-MM-dd');
                    const presetVals = [startStr, endStr];
                    if (
                      newCriterion.values[0] !== startStr ||
                      newCriterion.values[1] !== endStr
                    ) {
                      setTimeout(() => {
                        setNewCriterion((prev) => ({ ...prev, values: presetVals }));
                        formik.setFieldValue('values', presetVals);
                      }, 0);
                    }
                    return (
                      <TextField
                        size="small"
                        fullWidth
                        disabled
                        value={`${startStr} to ${endStr}`}
                        className="criteria-input"
                      />
                    );
                  }
                  if (isRange) {
                    return (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="criteria-between-row">
                          <DatePicker
                            format="DD/MM/YYYY"
                            disabled={disabled}
                            value={newCriterion.values[0] ? dayjs(newCriterion.values[0]) : null}
                            onChange={(d) => {
                              const v = d && d.isValid() ? d.format('YYYY-MM-DD') : '';
                              const next = [v, newCriterion.values[1] || ''];
                              const filtered = next.filter(Boolean);
                              setNewCriterion((prev) => ({ ...prev, values: filtered.length === 2 ? next : filtered }));
                              formik.setFieldValue('values', filtered);
                            }}
                            slotProps={{ textField: { size: 'small', fullWidth: true, className: 'criteria-input' } }}
                          />
                          <DatePicker
                            format="DD/MM/YYYY"
                            disabled={disabled}
                            value={newCriterion.values[1] ? dayjs(newCriterion.values[1]) : null}
                            onChange={(d) => {
                              const v = d && d.isValid() ? d.format('YYYY-MM-DD') : '';
                              const next = [newCriterion.values[0] || '', v];
                              const filtered = next.filter(Boolean);
                              setNewCriterion((prev) => ({ ...prev, values: filtered.length === 2 ? next : filtered }));
                              formik.setFieldValue('values', filtered);
                            }}
                            slotProps={{ textField: { size: 'small', fullWidth: true, className: 'criteria-input' } }}
                          />
                        </div>
                      </LocalizationProvider>
                    );
                  }
                  return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        disabled={disabled}
                        value={newCriterion.values[0] ? dayjs(newCriterion.values[0]) : null}
                        onChange={(d) => {
                          const v = d && d.isValid() ? d.format('YYYY-MM-DD') : '';
                          setNewCriterion((prev) => ({ ...prev, values: v ? [v] : [] }));
                          formik.setFieldValue('values', v ? [v] : []);
                        }}
                        slotProps={{ textField: { size: 'small', fullWidth: true, className: 'criteria-input' } }}
                      />
                    </LocalizationProvider>
                  );
                }

                return isSingleSelect() ? (
                // Single select dropdown for "Is" operation on Client/Recruiter
                <FormControl fullWidth error={newCriterion.values.length === 0}>
                  <Select
                    value={newCriterion.values[0] || ''}
                    onChange={(e) =>
                      setNewCriterion({
                        ...newCriterion,
                        values: [e.target.value],
                      })
                    }
                    displayEmpty
                    disabled={!newCriterion.filterBy || !newCriterion.operation}
                    className="criteria-select"
                    onClose={() => setDropdownSearch('')}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: '#999' }}>Select Value</span>
                        );
                      }
                      const options = getValueOptions();
                      const option = options.find((opt) => opt.id === selected);
                      return option ? option.name : selected;
                    }}
                    MenuProps={{
                      variant: 'menu',
                      PaperProps: {
                        style: {
                          maxHeight: 350,
                        },
                      },
                      autoFocus: false,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {/* Search input */}
                    <ListSubheader
                      style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        zIndex: 1,
                        padding: '8px 12px',
                      }}
                    >
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search..."
                        fullWidth
                        value={dropdownSearch}
                        onChange={(e) => setDropdownSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                fontSize="small"
                                style={{ color: '#999' }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ListSubheader>
                    {getValueOptions()
                      .filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(dropdownSearch.toLowerCase())
                      )
                      .map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {criteria.length === 0 && (
                    <ErrorMessage name="values" component="div" className="error-message" />
                  )}
                </FormControl>
              ) : (
                // Multi-select dropdown for "Contains", "Not In" and other operations
                <FormControl fullWidth error={newCriterion.values.length === 0}>
                  <Select
                    multiple
                    value={newCriterion.values}
                    onChange={(e) => {
                      handleValuesChange(e);
                      formik.setFieldValue('values', e.target.value);}}
                    displayEmpty
                    disabled={!newCriterion.filterBy || !newCriterion.operation}
                    className="criteria-select"
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <span style={{ color: '#999' }}>Select Value(s)</span>
                        );
                      }
                      return renderSelectedValues(selected);
                    }}
                    onClose={() => setDropdownSearch('')}
                    MenuProps={{
                      variant: 'menu',
                      PaperProps: {
                        style: {
                          maxHeight: 350,
                        },
                      },
                      autoFocus: false,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {/* Search input */}
                    <ListSubheader
                      style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        zIndex: 1,
                        padding: '8px 12px',
                      }}
                    >
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search..."
                        fullWidth
                        value={dropdownSearch}
                        onChange={(e) => setDropdownSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                fontSize="small"
                                style={{ color: '#999' }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ListSubheader>
                    {getValueOptions()
                      .filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(dropdownSearch.toLowerCase())
                      )
                      .map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          <Checkbox
                            checked={newCriterion.values.indexOf(option.id) > -1}
                          />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                  </Select>
                  {criteria.length === 0 && (
                    <ErrorMessage name="values" component="div" className="error-message" />
                  )}
                </FormControl>
              );
              })()}
            </div>
          </div>

          <div className="criteria-btn-wrapper">
            {(newCriterion.filterBy ||
              newCriterion.operation ||
              newCriterion.values.length > 0) && (
              <button
                className="clear-criteria-btn"
                onClick={handleClearCriteriaForm}
                type="button"
              >
                Clear
              </button>
            )}
            <button
              className="add-criteria-btn"
              onClick={() => handleAddCriteria(formik)}
              disabled={
                !newCriterion.filterBy ||
                !newCriterion.operation ||
                (newCriterion.operation === 'Between'
                  ? newCriterion.values.filter((v) => v !== '' && v !== undefined && v !== null).length < 2
                  : newCriterion.values.filter((v) => v !== '' && v !== undefined && v !== null).length === 0)
              }
            >
              + Add criteria
            </button>
          </div>

          {/* Added Criteria */}
          {criteria.length > 0 && (
            <div className="added-criteria-section">
              <h4 className="added-criteria-title">Added Criteria</h4>
              {criteria.map((criterion) => (
                <div key={criterion.id} className="criterion-item">
                  <span className="criterion-text">
                    Criteria : {criterion.filterBy} | Operation :{' '}
                    {criterion.operation} | Value : {criterion.valuesLabel}
                  </span>
                  <IconButton
                    onClick={() => handleRemoveCriteria(criterion.id)}
                    className="remove-criterion-btn"
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
          </>
          )}
          <ErrorMessage name="criteria" component="div" className="error-message" />
        </div>

        {/* Select Fields Section */}
        <div className="select-fields-section">
          <div className="select-fields-header">
            <h3 className="section-title">Select Fields</h3>
            {reportType && (newCriterion.filterBy || criteria.length > 0) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCustomFieldChecked}
                    onChange={handleCustomFieldToggle}
                    className="field-checkbox"
                  />
                }
                label={
                  <span className="custom-field-toggle-label">
                    Custom Fields
                    {customFieldsLoading && (
                      <span className="custom-fields-loading">Loading...</span>
                    )}
                  </span>
                }
                className="field-checkbox-label"
              />
            )}
          </div>

          {!reportType || (!newCriterion.filterBy && criteria.length === 0) ? (
            <p className="no-report-type-message">
              {!reportType
                ? 'Please select a report type to see available fields'
                : 'Please select a filter criteria to see available fields'}
            </p>
          ) : (
                <div className={reportType === reportTypesEnum.CANDIDATE ? "fields-four-col-layout" : ''}>
                  {reportConfig?.layout?.sections?.map((section) => {
                    const fields = getSectionFields(section.key);
                    const columns = reportConfig?.layout?.columns || 2;
                    return (
                      <div className="fields-category" key={section.key}>
                        <h4 className="fields-category-heading">{section.title}</h4>
                        <div className={`fields-${columns}-col`}>
                          {renderSelectFieldsUI(formik, fields, columns)}
                        </div>
                      </div>
                    );
                  })}
                </div>
          )}
          <ErrorMessage name="selectedFields" component="div" className="error-message" />
        </div>
        {/* Selected Fields Summary */}
        <div className="selected-fields-summary">
          <span className="summary-label">Selected Fields:</span>
          <span className="summary-value">
            {selectedFields.length > 0
              ? selectedFields.map((f) => f.displayName).join(', ')
              : 'No fields selected'}
          </span>
          {selectedFields.length > 0 && (
            <button className="clear-btn" onClick={handleClearFields}>
              Clear
            </button>
          )}
        </div>

        {/* Table Preview */}
        {selectedFields.length > 0 && (
          <div className="table-preview-section">
            <div className="table-preview-header">
              {selectedFields.map((field, index) => (
                <div
                  key={field.mappingName}
                  className={`table-header-cell ${
                    dragOverIndex === index && draggingIndex !== index
                      ? 'drag-over'
                      : ''
                  } ${draggingIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="drag-handle">::</span>
                  <span>{field.displayName}</span>
                </div>
              ))}
            </div>
            <div className="table-preview-body">
              <p className="preview-message">
                Table data will appear here when report is requested
              </p>
            </div>
          </div>
        )}
        {/* Request Report Button */}
        <div className="report-action-section">
          <button
            className="request-report-btn"
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            Save
          </button>
        </div>
      </div>
      )}
    </Formik>
      {showScheduleReport && (
        <ScheduleReport
          data={reportData}
          onReceiveProps={(val) => {
            setShowScheduleReport(val);
          }}
        />
      )}
    </>
);
}

export default ScheduleReportsDashboard;